import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, CreditCard, TrendingUp, Info, AlertTriangle, DollarSign } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area } from 'recharts';

const STORAGE_KEY = 'credit_card_data';

const CreditCardCalculator = () => {
  const [balance, setBalance] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).balance : 5000;
  });
  
  const [apr, setApr] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).apr : 18.99;
  });
  
  const [minimumPayment, setMinimumPayment] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).minimumPayment : 100;
  });
  
  const [additionalPayment, setAdditionalPayment] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).additionalPayment : 200;
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      balance,
      apr,
      minimumPayment,
      additionalPayment
    }));
  }, [balance, apr, minimumPayment, additionalPayment]);

  const calculatePayoff = (monthlyPayment) => {
    let currentBalance = parseFloat(balance) || 0;
    const annualRate = (parseFloat(apr) || 0) / 100;
    const monthlyRate = annualRate / 12;
    const payment = parseFloat(monthlyPayment) || 0;
    
    if (payment <= 0 || currentBalance <= 0) {
      return { months: 0, totalPaid: 0, totalInterest: 0 };
    }
    
    let months = 0;
    let totalInterest = 0;
    const maxMonths = 600; // Safety limit
    
    while (currentBalance > 0 && months < maxMonths) {
      const interestCharge = currentBalance * monthlyRate;
      totalInterest += interestCharge;
      
      const principalPayment = Math.min(payment - interestCharge, currentBalance);
      
      if (principalPayment <= 0) {
        // Payment doesn't cover interest
        return { months: maxMonths, totalPaid: Infinity, totalInterest: Infinity };
      }
      
      currentBalance -= principalPayment;
      months++;
    }
    
    const totalPaid = parseFloat(balance) + totalInterest;
    return { months, totalPaid, totalInterest };
  };

  const calculateResults = () => {
    const minPayment = parseFloat(minimumPayment) || 0;
    const totalPayment = minPayment + (parseFloat(additionalPayment) || 0);
    
    const minResults = calculatePayoff(minPayment);
    const totalResults = calculatePayoff(totalPayment);
    
    const monthsSaved = minResults.months - totalResults.months;
    const interestSaved = minResults.totalInterest - totalResults.totalInterest;
    const totalSaved = minResults.totalPaid - totalResults.totalPaid;
    
    return {
      minPayoff: minResults,
      totalPayoff: totalResults,
      monthsSaved: Math.max(0, monthsSaved),
      interestSaved: Math.max(0, interestSaved),
      totalSaved: Math.max(0, totalSaved),
      totalPayment
    };
  };

  const generatePayoffTimeline = () => {
    const results = calculateResults();
    const maxMonths = Math.min(Math.max(results.minPayoff.months, results.totalPayoff.months), 120);
    
    let minBalance = parseFloat(balance) || 0;
    let totalBalance = parseFloat(balance) || 0;
    const annualRate = (parseFloat(apr) || 0) / 100;
    const monthlyRate = annualRate / 12;
    const minPayment = parseFloat(minimumPayment) || 0;
    const totalPayment = results.totalPayment;
    
    const data = [];
    
    for (let month = 0; month <= maxMonths; month++) {
      data.push({
        month,
        minPayment: Math.max(0, Math.round(minBalance)),
        withExtra: Math.max(0, Math.round(totalBalance))
      });
      
      if (month === maxMonths) break;
      
      // Calculate next month's balances
      if (minBalance > 0) {
        const minInterest = minBalance * monthlyRate;
        const minPrincipal = Math.min(minPayment - minInterest, minBalance);
        if (minPrincipal > 0) minBalance -= minPrincipal;
      }
      
      if (totalBalance > 0) {
        const totalInterest = totalBalance * monthlyRate;
        const totalPrincipal = Math.min(totalPayment - totalInterest, totalBalance);
        if (totalPrincipal > 0) totalBalance -= totalPrincipal;
      }
    }
    
    return data;
  };

  const generateInterestVsPrincipal = () => {
    const results = calculateResults();
    return [
      {
        strategy: 'Minimum Only',
        principal: parseFloat(balance) || 0,
        interest: results.minPayoff.totalInterest
      },
      {
        strategy: 'With Extra Payment',
        principal: parseFloat(balance) || 0,
        interest: results.totalPayoff.totalInterest
      }
    ];
  };

  const generatePaymentScenarios = () => {
    const basePayment = parseFloat(minimumPayment) || 0;
    const scenarios = [
      { name: 'Minimum', payment: basePayment },
      { name: '+$50', payment: basePayment + 50 },
      { name: '+$100', payment: basePayment + 100 },
      { name: '+$200', payment: basePayment + 200 },
      { name: '+$300', payment: basePayment + 300 }
    ];
    
    return scenarios.map(scenario => {
      const result = calculatePayoff(scenario.payment);
      return {
        name: scenario.name,
        payment: scenario.payment,
        months: result.months,
        totalInterest: result.totalInterest,
        totalPaid: result.totalPaid
      };
    });
  };

  const results = calculateResults();
  const timelineData = generatePayoffTimeline();
  const interestData = generateInterestVsPrincipal();
  const scenarioData = generatePaymentScenarios();

  const formatCurrency = (value) => {
    if (!isFinite(value)) return '$∞';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatMonths = (months) => {
    if (!isFinite(months) || months >= 600) return 'Never';
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    if (years === 0) return `${months} months`;
    if (remainingMonths === 0) return `${years} ${years === 1 ? 'year' : 'years'}`;
    return `${years}y ${remainingMonths}m`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-accent-light/20 to-background">
      <nav className="border-b border-border/40 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              </Link>
              <Separator orientation="vertical" className="h-6" />
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg gradient-accent flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-white" />
                </div>
                <span className="font-display text-lg font-semibold text-foreground">
                  Credit Card Payoff Calculator
                </span>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="calculator" className="space-y-6">
          <div className="overflow-x-auto scrollbar-hide -mx-4 px-4">
            <TabsList className="inline-flex w-auto min-w-full sm:min-w-0 bg-muted/50 p-1 h-auto">
              <TabsTrigger 
                value="calculator" 
                className="data-[state=active]:bg-accent data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <CreditCard className="w-4 h-4 mr-2" />
                Calculator & Results
              </TabsTrigger>
              <TabsTrigger 
                value="charts" 
                className="data-[state=active]:bg-accent data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Visual Analysis
              </TabsTrigger>
              <TabsTrigger 
                value="insights" 
                className="data-[state=active]:bg-accent data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <Info className="w-4 h-4 mr-2" />
                Strategies & Tips
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="calculator" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">Understanding Credit Card Debt</CardTitle>
                <CardDescription className="text-base leading-relaxed">
                  Credit card debt can grow quickly due to compound interest. This calculator shows how much you can save 
                  by paying more than the minimum and helps you create a realistic payoff strategy.
                </CardDescription>
              </CardHeader>
            </Card>

            <div className="grid lg:grid-cols-3 gap-6">
              <Card className="lg:col-span-1 border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Card Details</CardTitle>
                  <CardDescription>Enter your credit card information</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="balance" className="text-sm font-medium">
                      Outstanding Balance ($)
                    </Label>
                    <Input
                      id="balance"
                      type="number"
                      value={balance}
                      onChange={(e) => setBalance(e.target.value)}
                      placeholder="5000"
                      className="text-base"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="apr" className="text-sm font-medium">
                      Interest Rate (APR %)
                    </Label>
                    <Input
                      id="apr"
                      type="number"
                      value={apr}
                      onChange={(e) => setApr(e.target.value)}
                      placeholder="18.99"
                      step="0.01"
                      className="text-base"
                    />
                    <p className="text-xs text-muted-foreground">Annual percentage rate</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="minimumPayment" className="text-sm font-medium">
                      Minimum Payment ($)
                    </Label>
                    <Input
                      id="minimumPayment"
                      type="number"
                      value={minimumPayment}
                      onChange={(e) => setMinimumPayment(e.target.value)}
                      placeholder="100"
                      className="text-base"
                    />
                    <p className="text-xs text-muted-foreground">Required monthly minimum</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="additionalPayment" className="text-sm font-medium">
                      Additional Payment ($)
                    </Label>
                    <Input
                      id="additionalPayment"
                      type="number"
                      value={additionalPayment}
                      onChange={(e) => setAdditionalPayment(e.target.value)}
                      placeholder="200"
                      className="text-base"
                    />
                    <p className="text-xs text-muted-foreground">Extra amount to pay monthly</p>
                  </div>

                  <Button 
                    className="w-full gradient-accent hover:opacity-90 transition-opacity shadow-secondary"
                    onClick={() => setBalance(balance)}
                  >
                    <CreditCard className="w-4 h-4 mr-2" />
                    Calculate Payoff
                  </Button>
                </CardContent>
              </Card>

              <div className="lg:col-span-2 space-y-6">
                <Card className="border-border/50 shadow-md">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="font-display">Payoff Comparison</CardTitle>
                        <CardDescription>Minimum payment vs accelerated payoff</CardDescription>
                      </div>
                      {results.interestSaved > 0 && (
                        <Badge className="px-4 py-2 text-sm bg-secondary/20 text-secondary">
                          Save {formatCurrency(results.interestSaved)}
                        </Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className="p-6 rounded-xl bg-gradient-to-br from-destructive/10 to-card border border-destructive/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-destructive flex items-center justify-center">
                            <AlertTriangle className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Minimum Payment Only</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-destructive mb-1">
                          {formatMonths(results.minPayoff.months)}
                        </p>
                        <p className="text-sm text-muted-foreground">Time to payoff</p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-secondary-light to-card border border-secondary/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center">
                            <TrendingUp className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">With Extra Payment</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-secondary mb-1">
                          {formatMonths(results.totalPayoff.months)}
                        </p>
                        <p className="text-sm text-muted-foreground">Time to payoff</p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-warning-light to-card border border-warning/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-warning flex items-center justify-center">
                            <DollarSign className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Total Interest (Min)</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-warning">
                          {formatCurrency(results.minPayoff.totalInterest)}
                        </p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-accent-light to-card border border-accent/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-accent flex items-center justify-center">
                            <DollarSign className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Total Interest (Extra)</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-accent">
                          {formatCurrency(results.totalPayoff.totalInterest)}
                        </p>
                      </div>
                    </div>

                    <Separator className="my-6" />

                    <div className="p-6 rounded-xl bg-gradient-chart">
                      <div className="grid sm:grid-cols-2 gap-6">
                        <div>
                          <p className="text-sm font-medium text-muted-foreground mb-2">Time Saved</p>
                          <p className="text-2xl font-display font-bold text-secondary mb-1">
                            {formatMonths(results.monthsSaved)}
                          </p>
                          <p className="text-sm text-muted-foreground">By paying extra</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-muted-foreground mb-2">Interest Saved</p>
                          <p className="text-2xl font-display font-bold text-secondary mb-1">
                            {formatCurrency(results.interestSaved)}
                          </p>
                          <p className="text-sm text-muted-foreground">Total savings</p>
                        </div>
                      </div>
                    </div>

                    {parseFloat(minimumPayment) > 0 && 
                     parseFloat(balance) > 0 && 
                     parseFloat(minimumPayment) <= (parseFloat(balance) * ((parseFloat(apr) / 100) / 12)) && (
                      <div className="mt-4 p-4 rounded-lg bg-destructive/10 border border-destructive/30">
                        <div className="flex gap-3">
                          <AlertTriangle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="font-semibold text-destructive mb-1">Minimum Payment Trap!</p>
                            <p className="text-sm text-muted-foreground">
                              Your minimum payment doesn't cover the monthly interest. Your balance will never be paid off at this rate. 
                              Increase your payment to make progress.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="charts" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">Balance Reduction Timeline</CardTitle>
                <CardDescription className="text-base">
                  See how your balance decreases over time with different payment strategies
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={timelineData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis 
                        dataKey="month" 
                        label={{ value: 'Months', position: 'insideBottom', offset: -5 }}
                        stroke="hsl(var(--muted-foreground))"
                      />
                      <YAxis 
                        label={{ value: 'Balance ($)', angle: -90, position: 'insideLeft' }}
                        stroke="hsl(var(--muted-foreground))"
                        tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                        formatter={(value) => formatCurrency(value)}
                      />
                      <Legend />
                      <Area 
                        type="monotone" 
                        dataKey="minPayment" 
                        stroke="hsl(var(--chart-5))" 
                        fill="hsl(var(--chart-5) / 0.3)"
                        strokeWidth={3}
                        name="Minimum Payment"
                      />
                      <Area 
                        type="monotone" 
                        dataKey="withExtra" 
                        stroke="hsl(var(--chart-2))" 
                        fill="hsl(var(--chart-2) / 0.3)"
                        strokeWidth={3}
                        name="With Extra Payment"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Interest vs Principal</CardTitle>
                  <CardDescription>
                    Compare how much you pay in interest vs your original debt
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={interestData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis 
                          dataKey="strategy" 
                          stroke="hsl(var(--muted-foreground))"
                        />
                        <YAxis 
                          stroke="hsl(var(--muted-foreground))"
                          tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))', 
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                          formatter={(value) => formatCurrency(value)}
                        />
                        <Legend />
                        <Bar dataKey="principal" fill="hsl(var(--chart-1))" name="Principal" stackId="a" />
                        <Bar dataKey="interest" fill="hsl(var(--chart-5))" name="Interest" stackId="a" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Payment Acceleration Impact</CardTitle>
                  <CardDescription>
                    How different extra payments affect your payoff time
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={scenarioData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis 
                          dataKey="name" 
                          stroke="hsl(var(--muted-foreground))"
                        />
                        <YAxis 
                          stroke="hsl(var(--muted-foreground))"
                          label={{ value: 'Months', angle: -90, position: 'insideLeft' }}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))', 
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                        />
                        <Bar dataKey="months" fill="hsl(var(--chart-2))" name="Payoff Time (Months)" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="insights" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">Debt Payoff Strategies & Tips</CardTitle>
                <CardDescription className="text-base">
                  Learn how to eliminate credit card debt faster and save thousands in interest
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-destructive">
                    The Minimum Payment Trap
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-4">
                    Paying only the minimum keeps you in debt for years. At your current rate of {formatCurrency(parseFloat(minimumPayment))} per month, 
                    you'll pay {formatCurrency(results.minPayoff.totalInterest)} in interest alone - that's{' '}
                    <strong className="text-destructive">
                      {((results.minPayoff.totalInterest / parseFloat(balance)) * 100).toFixed(0)}% more than your original debt!
                    </strong>
                  </p>
                  <div className="p-6 rounded-xl bg-gradient-to-br from-destructive/10 to-card border border-destructive/20">
                    <div className="grid sm:grid-cols-3 gap-4">
                      <div>
                        <p className="text-sm text-muted-foreground mb-1">Original Debt</p>
                        <p className="text-2xl font-display font-bold text-foreground">{formatCurrency(parseFloat(balance))}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground mb-1">Total Interest</p>
                        <p className="text-2xl font-display font-bold text-destructive">{formatCurrency(results.minPayoff.totalInterest)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground mb-1">You'll Pay</p>
                        <p className="text-2xl font-display font-bold text-warning">{formatCurrency(results.minPayoff.totalPaid)}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-secondary">
                    The Power of Extra Payments
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-4">
                    By paying an extra {formatCurrency(parseFloat(additionalPayment))} per month (total: {formatCurrency(results.totalPayment)}), you can:
                  </p>
                  <div className="space-y-4">
                    <div className="p-4 rounded-lg bg-secondary-light border-l-4 border-secondary">
                      <h4 className="font-semibold text-foreground mb-2">✅ Pay Off Debt {formatMonths(results.monthsSaved)} Faster</h4>
                      <p className="text-muted-foreground">
                        Reduce payoff time from {formatMonths(results.minPayoff.months)} to just {formatMonths(results.totalPayoff.months)}.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-secondary-light border-l-4 border-secondary">
                      <h4 className="font-semibold text-foreground mb-2">✅ Save {formatCurrency(results.interestSaved)} in Interest</h4>
                      <p className="text-muted-foreground">
                        That's money you can invest, save, or use for things you actually want instead of giving it to credit card companies.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-secondary-light border-l-4 border-secondary">
                      <h4 className="font-semibold text-foreground mb-2">✅ Improve Your Credit Score</h4>
                      <p className="text-muted-foreground">
                        Lower credit utilization and consistent payments boost your credit score, helping you qualify for better rates in the future.
                      </p>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-accent">
                    Proven Debt Payoff Strategies
                  </h3>
                  <div className="space-y-4">
                    <div className="p-4 rounded-lg bg-primary-light border-l-4 border-primary">
                      <h4 className="font-semibold text-foreground mb-2">1. The Avalanche Method</h4>
                      <p className="text-muted-foreground">
                        Pay minimums on all cards, then put all extra money toward the card with the highest interest rate. 
                        This saves the most money on interest over time.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-accent-light border-l-4 border-accent">
                      <h4 className="font-semibold text-foreground mb-2">2. The Snowball Method</h4>
                      <p className="text-muted-foreground">
                        Pay minimums on all cards, then put all extra money toward the smallest balance. 
                        Quick wins build momentum and motivation.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-purple-light border-l-4 border-purple">
                      <h4 className="font-semibold text-foreground mb-2">3. Balance Transfer</h4>
                      <p className="text-muted-foreground">
                        Transfer high-interest debt to a 0% APR card (usually 12-21 months). 
                        All payments go to principal during the promotional period.
                      </p>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-warning">
                    Smart Money Moves
                  </h3>
                  <div className="space-y-3 text-base">
                    <div className="flex gap-3">
                      <span className="text-secondary font-semibold flex-shrink-0">✅</span>
                      <div>
                        <strong className="text-foreground">Stop New Charges:</strong> Don't add to the balance while paying it off. 
                        Cut up the card if necessary or freeze it to avoid temptation.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-secondary font-semibold flex-shrink-0">✅</span>
                      <div>
                        <strong className="text-foreground">Pay Bi-Weekly:</strong> Split your monthly payment in half and pay every two weeks. 
                        You'll make an extra payment per year and reduce interest faster.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-secondary font-semibold flex-shrink-0">✅</span>
                      <div>
                        <strong className="text-foreground">Use Windfalls:</strong> Tax refunds, bonuses, or gifts should go directly to your highest-interest debt. 
                        These lump sums dramatically accelerate payoff.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-secondary font-semibold flex-shrink-0">✅</span>
                      <div>
                        <strong className="text-foreground">Negotiate Lower Rates:</strong> Call your card issuer and ask for a lower APR. 
                        If you have good payment history, they may reduce your rate by 2-5%.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-secondary font-semibold flex-shrink-0">✅</span>
                      <div>
                        <strong className="text-foreground">Find Extra Money:</strong> Cancel unused subscriptions, sell items you don't need, 
                        or pick up a side gig temporarily to boost your payment amount.
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-purple">
                    Avoiding Future Debt
                  </h3>
                  <div className="space-y-3 text-base text-muted-foreground leading-relaxed">
                    <div className="flex gap-3">
                      <span className="text-warning font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Build an Emergency Fund:</strong> Save $1,000 minimum to avoid using cards for unexpected expenses. 
                        This breaks the debt cycle.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-warning font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Pay in Full Monthly:</strong> Once debt-free, commit to paying the full statement balance every month. 
                        Enjoy rewards without interest charges.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-warning font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Track Your Spending:</strong> Use budgeting apps to monitor where money goes. 
                        Awareness prevents overspending and debt accumulation.
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default CreditCardCalculator;
